import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ComposedChart, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Legend,
} from 'recharts';
import api from '../../services/api';
import PageHeader from '../../components/common/PageHeader';
import styles from './Dashboard.module.css';

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  blue:      '#1B4FD8',
  teal:      '#5DCAA5',
  tealDark:  '#1D9E75',
  amber:     '#EF9F27',
  amberDark: '#854F0B',
  red:       '#E24B4A',
  redLight:  '#FCEBEB',
  greenDark: '#3B6D11',
};

// Distinct colors for modality lines
const LINE_COLORS = ['#1B4FD8','#5DCAA5','#EF9F27','#E24B4A','#8B5CF6','#06B6D4','#F97316','#10B981'];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (v, blank = '—') =>
  v != null
    ? `$${parseFloat(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : blank;
const fmtPct  = (v) => v != null ? `${parseFloat(v).toFixed(1)}%` : '—';
const fmtK    = (v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${Math.round(v)}`;

function paceLabel(pct) {
  if (pct == null) return '—';
  if (pct >= 95) return 'On target';
  if (pct >= 80) return 'On track';
  if (pct >= 65) return 'Slightly behind';
  return 'Behind — act now';
}

function barColor(pct) {
  if (pct == null) return '#e5e7eb';
  if (pct >= 90) return C.amber;
  if (pct >= 60) return C.amber;
  return C.red;
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ pct }) {
  if (pct == null)  return <span className={styles.badgeNeutral}>No Target</span>;
  if (pct >= 90)    return <span className={styles.badgeGreen}>On Track</span>;
  if (pct >= 60)    return <span className={styles.badgeAmber}>Watch</span>;
  return               <span className={styles.badgeRed}>Behind</span>;
}

// ── Hero banner ───────────────────────────────────────────────────────────────
const HERO_TABS = [
  { key: 'hourly',  label: 'Hourly' },
  { key: 'daily',   label: 'Daily' },
  { key: 'weekly',  label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
];

function HeroBanner({ data }) {
  const [tab, setTab] = useState('daily');

  const hrs          = Math.max(new Date().getHours() + new Date().getMinutes() / 60, 0.5);
  const hourlyEarned = parseFloat(data.today_revenue || 0) / hrs;
  const hTarget      = data.hourly_target  ? parseFloat(data.hourly_target)  : null;
  const hPct         = hTarget ? (hourlyEarned / hTarget * 100) : null;

  const PERIODS = {
    hourly: {
      heading:  "This hour's revenue",
      revenue:  hourlyEarned,
      target:   hTarget,
      tgtLabel: hTarget ? `Hourly target: ${fmt(hTarget)}` : null,
      pct:      hPct,
      meta: [
        { label: 'Today so far', val: fmt(data.today_revenue) },
        { label: 'Run rate',     val: `$${hourlyEarned.toFixed(2)}/hr` },
        { label: 'Pace',         val: paceLabel(hPct) },
      ],
    },
    daily: {
      heading:  "Today's revenue",
      revenue:  parseFloat(data.today_revenue || 0),
      target:   data.daily_target  ? parseFloat(data.daily_target)  : null,
      tgtLabel: data.daily_target  ? `Daily target: ${fmt(data.daily_target)}`  : null,
      pct:      data.daily_pct,
      meta: [
        { label: 'Run rate',    val: `$${hourlyEarned.toFixed(2)}/hr` },
        { label: 'This week',   val: fmt(data.week_revenue) },
        { label: 'This month',  val: fmt(data.month_revenue) },
        data.daily_target ? {
          label: 'Gap',
          val: (() => {
            const g = parseFloat(data.daily_target) - parseFloat(data.today_revenue || 0);
            return g <= 0 ? `${fmt(Math.abs(g))} over` : `${fmt(g)} remaining`;
          })(),
          style: (parseFloat(data.daily_target) - parseFloat(data.today_revenue || 0)) <= 0
            ? { color: C.teal } : { color: C.amber },
        } : null,
        { label: 'Pace',        val: paceLabel(data.daily_pct) },
      ].filter(Boolean),
    },
    weekly: {
      heading:  "This week's revenue",
      revenue:  parseFloat(data.week_revenue  || 0),
      target:   data.weekly_target ? parseFloat(data.weekly_target) : null,
      tgtLabel: data.weekly_target ? `Weekly target: ${fmt(data.weekly_target)}` : null,
      pct:      data.weekly_pct,
      meta: [
        { label: 'Days elapsed', val: `${data.days_elapsed_this_week} of 7` },
        { label: 'Daily avg',    val: fmt(parseFloat(data.week_revenue || 0) / (data.days_elapsed_this_week || 1)) },
        { label: 'This month',   val: fmt(data.month_revenue) },
        { label: 'Pace',         val: paceLabel(data.weekly_pct) },
      ],
    },
    monthly: {
      heading:  "This month's revenue",
      revenue:  parseFloat(data.month_revenue || 0),
      target:   data.monthly_target ? parseFloat(data.monthly_target) : null,
      tgtLabel: data.monthly_target ? `Monthly target: ${fmt(data.monthly_target)}` : null,
      pct:      data.monthly_pct,
      meta: [
        { label: 'Days elapsed', val: `${data.days_elapsed_this_month} of 30` },
        { label: 'Daily avg',    val: fmt(parseFloat(data.month_revenue || 0) / (data.days_elapsed_this_month || 1)) },
        { label: 'This week',    val: fmt(data.week_revenue) },
        { label: 'Pace',         val: paceLabel(data.monthly_pct) },
      ],
    },
  };

  const p        = PERIODS[tab];
  const fill     = p.pct != null && p.pct >= 90 ? C.teal
                 : p.pct != null && p.pct >= 60 ? C.amber
                 : p.pct != null                 ? C.red
                 :                                 C.teal;
  const barWidth = p.pct != null ? Math.min(100, Math.max(0, p.pct)) : 0;

  return (
    <div className={styles.hero}>
      <div className={styles.heroTop}>
        {/* Left: heading + value + target */}
        <div>
          <div className={styles.heroPeriod}>{p.heading}</div>
          <div className={styles.heroVal}>{fmt(p.revenue)}</div>
          {p.tgtLabel && <div className={styles.heroTarget}>{p.tgtLabel}</div>}
        </div>

        {/* Right: tabs (top) + percentage (bottom) */}
        <div className={styles.heroRight}>
          <div className={styles.heroTabs}>
            {HERO_TABS.map(t => (
              <button
                key={t.key}
                className={`${styles.heroTab} ${tab === t.key ? styles.heroTabActive : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className={styles.heroPctBox}>
            {p.pct != null ? (
              <>
                <div className={styles.heroPct} style={{ color: fill }}>{fmtPct(p.pct)}</div>
                <div className={styles.heroPctLbl}>of target reached</div>
              </>
            ) : (
              <StatusBadge pct={null} />
            )}
          </div>
        </div>
      </div>

      <div className={styles.heroProgressTrack}>
        <div className={styles.heroProgressFill} style={{ width: `${barWidth}%`, background: fill }} />
      </div>

      <div className={styles.heroMeta}>
        {p.meta.map((m, i) => (
          <div key={i} className={styles.hmItem}>
            {m.label}: <span style={m.style || {}}>{m.val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Four KPI cards ────────────────────────────────────────────────────────────
function KpiCard({ period, earned, target, pct }) {
  const color = barColor(pct);
  const width = pct != null ? Math.min(100, Math.max(0, pct)) : 0;
  return (
    <div className={styles.kpiCard}>
      <div className={styles.kpiPeriod}>{period}</div>
      <div className={styles.kpiRow}>
        <span className={styles.kpiLbl}>Earned</span>
        <span className={styles.kpiVal}>{fmt(earned)}</span>
      </div>
      <div className={styles.kpiRow}>
        <span className={styles.kpiLbl}>Target</span>
        <span className={`${styles.kpiVal} ${styles.kpiTgt}`}>{target ? fmt(target) : '—'}</span>
      </div>
      <div className={styles.kpiRow}>
        <span className={styles.kpiLbl}>Expenses</span>
        <span className={`${styles.kpiVal} ${styles.kpiExp}`}>—</span>
      </div>
      <div className={styles.kpiBarRow}>
        <div className={styles.kpiTrack}>
          <div className={styles.kpiFill} style={{ width: `${width}%`, background: color }} />
        </div>
        <span className={styles.kpiPct} style={{ color }}>
          {pct != null ? `${Math.round(pct)}%` : '—'}
        </span>
      </div>
    </div>
  );
}

function FourKpiCards({ data }) {
  const hrs       = Math.max(new Date().getHours() + new Date().getMinutes() / 60, 0.5);
  const hourlyEarned = parseFloat(data.today_revenue || 0) / hrs;
  const hTarget   = data.hourly_target ? parseFloat(data.hourly_target) : null;
  const hPct      = hTarget ? (hourlyEarned / hTarget * 100) : null;

  const cards = [
    { period: 'Hourly',  earned: hourlyEarned,                     target: hTarget,                                        pct: hPct },
    { period: 'Daily',   earned: parseFloat(data.today_revenue||0), target: data.daily_target   ? parseFloat(data.daily_target)   : null, pct: data.daily_pct },
    { period: 'Weekly',  earned: parseFloat(data.week_revenue ||0), target: data.weekly_target  ? parseFloat(data.weekly_target)  : null, pct: data.weekly_pct },
    { period: 'Monthly', earned: parseFloat(data.month_revenue||0), target: data.monthly_target ? parseFloat(data.monthly_target) : null, pct: data.monthly_pct },
  ];

  return (
    <div className={styles.fourGrid}>
      {cards.map(c => <KpiCard key={c.period} {...c} />)}
    </div>
  );
}

// ── Hourly revenue vs expenses chart ──────────────────────────────────────────
function HourlyRevenueChart({ data }) {
  const now         = new Date();
  const currentHour = now.getHours();
  const startHour   = 8;  // business day begins at 8am
  const endHour     = Math.min(currentHour, 18);

  // Hours elapsed since 8am (minimum 0.5 so we don't divide by zero)
  const hoursElapsed  = Math.max(currentHour - startHour + now.getMinutes() / 60, 0.5);
  const todayRevenue  = parseFloat(data.today_revenue || 0);
  const hourlyRate    = todayRevenue / hoursElapsed;
  const hourlyTarget  = data.hourly_target ? parseFloat(data.hourly_target) : null;

  // Build one bar per business hour up to now
  const chartData = [];
  for (let h = startHour; h <= endHour; h++) {
    const label = h === 12 ? '12pm' : h > 12 ? `${h - 12}pm` : `${h}am`;
    chartData.push({ hour: label, revenue: parseFloat(hourlyRate.toFixed(2)), expenses: 0 });
  }
  if (chartData.length === 0) {
    chartData.push({ hour: 'Now', revenue: parseFloat(hourlyRate.toFixed(2)), expenses: 0 });
  }

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHdr}>
        <div>
          <span className={styles.panelTitle}>Hourly revenue vs expenses — today</span>
          <span className={styles.panelSub} style={{ marginLeft: 8 }}>estimated from today's total</span>
        </div>
        <div className={styles.hrlyLegend}>
          <span className={styles.hrlyLegItem}>
            <span className={styles.legendDot} style={{ background: C.blue }} />Revenue
          </span>
          <span className={styles.hrlyLegItem}>
            <span className={styles.legendDot} style={{ background: '#FCEBEB', border: '1px solid #E24B4A', boxSizing: 'border-box' }} />Expenses
          </span>
          <span className={styles.hrlyLegItem}>
            <span className={styles.legendDotLine} />Target
          </span>
        </div>
      </div>

      {todayRevenue === 0 ? (
        <div className={styles.chartEmpty}>No revenue recorded today yet.</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chartData} barCategoryGap="28%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis dataKey="hour" fontSize={11} tick={{ fill: '#888780' }} axisLine={false} tickLine={false} />
            <YAxis fontSize={11} tick={{ fill: '#888780' }} tickFormatter={fmtK} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
              formatter={(v, name) => [fmt(v), name === 'revenue' ? 'Revenue' : 'Expenses']}
            />
            <Bar dataKey="revenue"  fill={C.blue}    radius={[3, 3, 0, 0]} name="revenue"  barSize={20} />
            <Bar dataKey="expenses" fill="#FCEBEB" stroke="#E24B4A" strokeWidth={1} radius={[3, 3, 0, 0]} name="expenses" barSize={20} />
            {hourlyTarget != null && (
              <ReferenceLine
                y={hourlyTarget}
                stroke={C.amber}
                strokeDasharray="5 3"
                strokeWidth={2}
                label={{ value: 'Target', position: 'insideTopRight', fontSize: 11, fill: C.amberDark }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ── Smart alerts ──────────────────────────────────────────────────────────────
function generateAlerts(data) {
  const now          = new Date();
  const hrs          = Math.max(now.getHours() + now.getMinutes() / 60, 0.5);
  const hrsLeft      = Math.max(18 - now.getHours() - now.getMinutes() / 60, 0);
  const today        = parseFloat(data.today_revenue || 0);
  const dailyTarget  = data.daily_target  ? parseFloat(data.daily_target)  : null;
  const dailyPct     = data.daily_pct     != null ? parseFloat(data.daily_pct)     : null;
  const weeklyPct    = data.weekly_pct    != null ? parseFloat(data.weekly_pct)    : null;
  const monthlyPct   = data.monthly_pct   != null ? parseFloat(data.monthly_pct)   : null;
  const hourlyTarget = data.hourly_target ? parseFloat(data.hourly_target) : null;
  const runRate      = today / hrs;
  const alerts       = [];

  // No revenue yet today
  if (today === 0) {
    return [{ type: 'critical', text: 'No revenue recorded today', note: "Submit today's entry to start tracking" }];
  }

  // Daily target
  if (dailyTarget) {
    const gap = dailyTarget - today;
    if (dailyPct >= 100) {
      alerts.push({ type: 'success', text: 'Daily target reached!',
        note: `${fmt(Math.abs(gap))} over target — great work` });
    } else if (dailyPct >= 85) {
      alerts.push({ type: 'success', text: 'On pace for daily target',
        note: `${fmt(gap)} to go · ${fmtPct(dailyPct)} reached` });
    } else if (dailyPct >= 65) {
      const needPerHr = hrsLeft > 0 ? (gap / hrsLeft).toFixed(0) : '—';
      alerts.push({ type: 'warning', text: 'Slightly behind daily target',
        note: `${fmt(gap)} shortfall · need $${needPerHr}/hr to catch up` });
    } else {
      alerts.push({ type: 'critical', text: 'Behind daily target — act now',
        note: `${fmt(gap)} remaining · ${hrsLeft.toFixed(0)}h left today` });
    }
  }

  // Hourly run rate
  if (hourlyTarget) {
    const hrPct = (runRate / hourlyTarget) * 100;
    if (hrPct >= 100) {
      alerts.push({ type: 'success', text: 'Hourly rate above target',
        note: `$${runRate.toFixed(0)}/hr · ${hrPct.toFixed(0)}% of hourly target` });
    } else if (hrPct < 70) {
      alerts.push({ type: 'warning', text: 'Hourly rate below target',
        note: `Running $${runRate.toFixed(0)}/hr · target $${hourlyTarget.toFixed(0)}/hr` });
    }
  }

  // Weekly pace
  if (weeklyPct != null) {
    if (weeklyPct >= 95) {
      alerts.push({ type: 'success', text: 'Weekly pace on track',
        note: `${fmtPct(weeklyPct)} of pro-rated weekly target` });
    } else if (weeklyPct < 70) {
      alerts.push({ type: 'warning', text: 'Weekly pace needs attention',
        note: `Only ${fmtPct(weeklyPct)} of pro-rated weekly target` });
    }
  }

  // Monthly pace
  if (monthlyPct != null && monthlyPct < 70) {
    alerts.push({ type: 'warning', text: 'Monthly pace is slow',
      note: `${fmtPct(monthlyPct)} of pro-rated monthly target` });
  }

  // No targets configured
  if (!dailyTarget && !hourlyTarget) {
    alerts.push({ type: 'info', text: 'No revenue targets set',
      note: 'Set targets to unlock smart tracking & alerts' });
  }

  if (alerts.length === 0) {
    alerts.push({ type: 'success', text: 'All metrics on track', note: 'Keep up the great work!' });
  }

  return alerts;
}

const ALERT_COLORS = {
  critical: '#E24B4A',
  warning:  '#EF9F27',
  success:  '#5DCAA5',
  info:     '#6366f1',
};

function SmartAlerts({ data }) {
  const alerts    = generateAlerts(data);
  const warnCount = alerts.filter(a => a.type === 'critical' || a.type === 'warning').length;

  return (
    <div className={`${styles.chartCard} ${styles.alertsPanel}`}>
      <div className={styles.chartHdr}>
        <span className={styles.panelTitle}>Smart alerts</span>
        <span className={warnCount > 0 ? styles.badgeAmber : styles.badgeGreen}>
          {warnCount > 0 ? `${warnCount} alert${warnCount > 1 ? 's' : ''}` : 'All good'}
        </span>
      </div>

      <div className={styles.alertsList}>
        {alerts.map((a, i) => (
          <div key={i} className={styles.alertItem}>
            <span className={styles.alertDot} style={{ background: ALERT_COLORS[a.type] }} />
            <div>
              <div className={styles.alertText}>{a.text}</div>
              {a.note && <div className={styles.alertNote}>{a.note}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Revenue bar chart ─────────────────────────────────────────────────────────
function RevenueChart({ businessId, height = 185 }) {
  const [trends,  setTrends]  = useState([]);
  const [days,    setDays]    = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/${businessId}/trends/`, { params: { days } })
      .then(res => {
        if (res.data.success) {
          setTrends(
            res.data.data.trends
              .filter(t => t.actual_revenue != null)
              .map(t => ({
                date:    (() => { const p = t.date.split('-'); return `${p[2]}-${p[1]}`; })(),
                revenue: parseFloat(t.actual_revenue),
                target:  t.daily_target_val,
              }))
          );
        }
      })
      .catch(() => setTrends([]))
      .finally(() => setLoading(false));
  }, [businessId, days]);

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHdr}>
        <div>
          <span className={styles.panelTitle}>Revenue trend</span>
          <span className={styles.panelSub} style={{ marginLeft: 8 }}>daily actuals vs target</span>
        </div>
        <div className={styles.periodBtns}>
          {[7, 14, 30].map(d => (
            <button
              key={d}
              className={`${styles.periodBtn} ${days === d ? styles.periodBtnActive : ''}`}
              onClick={() => setDays(d)}
            >{d}d</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className={styles.chartLoading}>Loading…</div>
      ) : trends.length === 0 ? (
        <div className={styles.chartEmpty}>No revenue data yet. Submit daily entries to see trends.</div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={trends} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis dataKey="date" fontSize={11} tick={{ fill: '#888780' }} />
            <YAxis fontSize={11} tick={{ fill: '#888780' }} tickFormatter={fmtK} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
              formatter={(v, name) => [fmt(v), name === 'revenue' ? 'Revenue' : 'Expenses']}
            />
            <Bar dataKey="revenue" fill={C.blue} radius={[3, 3, 0, 0]} name="revenue" />
            {trends[0]?.target != null && (
              <ReferenceLine
                y={trends[0].target}
                stroke={C.amber}
                strokeDasharray="5 3"
                strokeWidth={2}
                label={{ value: 'Target', position: 'right', fontSize: 11, fill: C.amberDark }}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ── Income vs Expenses panel ──────────────────────────────────────────────────
function IncomeExpensesPanel({ data }) {
  const revenue = parseFloat(data.today_revenue || 0);
  const target  = data.daily_target ? parseFloat(data.daily_target) : null;
  const gap     = target != null ? target - revenue : null;
  const pct     = data.daily_pct;
  const revBarPct = target ? Math.min(100, (revenue / target) * 100) : 100;

  const rows = [
    {
      label: 'Gross revenue',
      val: fmt(revenue),
      valStyle: {},
      delta: data.weekly_pct != null
        ? `${parseFloat(data.weekly_pct) >= 0 ? '+' : ''}${parseFloat(data.weekly_pct).toFixed(1)}% vs weekly target`
        : null,
      deltaStyle: data.weekly_pct != null && parseFloat(data.weekly_pct) >= 0 ? styles.deltaGreen : styles.deltaRed,
    },
    {
      label: 'Total expenses',
      sub: 'Staff · supplies · overhead',
      val: '—',
      delta: 'Expense tracking coming soon',
      deltaStyle: styles.deltaNeutral,
    },
    {
      label: 'Net income',
      val: fmt(revenue),
      valStyle: { color: C.blue },
      delta: 'No expense data yet',
      deltaStyle: styles.deltaNeutral,
    },
    gap != null ? {
      label: 'Gap to target',
      val: gap <= 0 ? `+${fmt(Math.abs(gap))}` : fmt(gap),
      valStyle: { color: gap <= 0 ? C.tealDark : C.amberDark },
      delta: gap <= 0 ? 'Target exceeded' : `${fmtPct(pct)} reached`,
      deltaStyle: gap <= 0 ? styles.deltaGreen : styles.deltaAmber,
    } : null,
  ].filter(Boolean);

  return (
    <div className={styles.eviPanel}>
      <div className={styles.panelHdr}>
        <span className={styles.panelTitle}>Income vs Expenses</span>
        <span className={styles.panelSub}>Today</span>
      </div>
      {rows.map((row, i) => (
        <div key={i} className={styles.eviRow}>
          <div>
            <div className={styles.eviLbl}>{row.label}</div>
            {row.sub && <div className={styles.eviSub}>{row.sub}</div>}
          </div>
          <div className={styles.eviRight}>
            <div className={styles.eviVal} style={row.valStyle}>{row.val}</div>
            {row.delta && <div className={`${styles.eviDelta} ${row.deltaStyle}`}>{row.delta}</div>}
          </div>
        </div>
      ))}
      {target && (
        <div className={styles.gapBarWrap}>
          <div className={styles.gapLblRow}>
            <span>Revenue vs target</span>
            <span>{fmt(revenue)} of {fmt(target)}</span>
          </div>
          <div className={styles.gapTrack}>
            <div className={styles.gapRevenue} style={{ width: `${revBarPct}%` }} />
          </div>
          <div className={styles.gapLegend}>
            <span><span className={styles.legendDot} style={{ background: C.blue }} />Revenue</span>
            <span><span className={styles.legendDot} style={{ background: '#e5e7eb' }} />Remaining</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Modality / field trend chart ──────────────────────────────────────────────
function ModalityTrend({ businessId, height = 185 }) {
  const [trendData,  setTrendData]  = useState(null);
  const [days,       setDays]       = useState(14);
  const [loading,    setLoading]    = useState(true);
  const [templates,  setTemplates]  = useState([]);
  const [templateId, setTemplateId] = useState('');

  // Fetch available templates for the business once
  useEffect(() => {
    if (!businessId) return;
    api.get(`/businesses/${businessId}/templates/`)
      .then(res => {
        if (res.data.success) {
          const active = (res.data.data || []).filter(t => t.is_active);
          setTemplates(active);
          // Auto-select first active template
          if (active.length > 0) setTemplateId(active[0].id);
        }
      })
      .catch(() => {});
  }, [businessId]);

  // Fetch trend data whenever business, days, or template selection changes
  useEffect(() => {
    if (!businessId) return;
    setLoading(true);
    const params = { days };
    if (templateId) params.template_id = templateId;
    api.get(`/analytics/${businessId}/field-trends/`, { params })
      .then(res => { if (res.data.success) setTrendData(res.data.data); })
      .catch(() => setTrendData(null))
      .finally(() => setLoading(false));
  }, [businessId, days, templateId]);

  const hasData = trendData?.field_names?.length > 0 && trendData?.series?.length > 0;

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHdr}>
        <div>
          <span className={styles.panelTitle}>Modality trend</span>
          <span className={styles.panelSub} style={{ marginLeft: 8 }}>daily totals per field</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {templates.length > 1 && (
            <select
              value={templateId}
              onChange={e => setTemplateId(e.target.value)}
              className={styles.templateSelect}
              title="Filter by template"
            >
              <option value="">All templates</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name || `Version ${t.version}`}
                </option>
              ))}
            </select>
          )}
          <div className={styles.periodBtns}>
            {[7, 14, 30].map(d => (
              <button
                key={d}
                className={`${styles.periodBtn} ${days === d ? styles.periodBtnActive : ''}`}
                onClick={() => setDays(d)}
              >{d}d</button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className={styles.chartLoading}>Loading…</div>
      ) : !hasData ? (
        <div className={styles.chartEmpty}>No field data yet. Submit grid-format entries to see modality trends.</div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={trendData.series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis dataKey="date" fontSize={11} tick={{ fill: '#888780' }} />
            <YAxis fontSize={11} tick={{ fill: '#888780' }} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              iconType="circle"
              iconSize={8}
            />
            {trendData.field_names.map((name, i) => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={LINE_COLORS[i % LINE_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ── Inline target form ────────────────────────────────────────────────────────
function TargetForm({ businessId, data, onSaved, onCancel }) {
  const [hourly,   setHourly]   = useState(data.hourly_target   || '');
  const [daily,    setDaily]    = useState(data.daily_target    || '');
  const [weekly,   setWeekly]   = useState(data.weekly_target   || '');
  const [monthly,  setMonthly]  = useState(data.monthly_target  || '');
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  function handleHourlyChange(val) {
    setHourly(val);
    const n = parseFloat(val);
    if (!isNaN(n) && n > 0) {
      setDaily((n * 8).toFixed(2));
      setWeekly((n * 8 * 7).toFixed(2));
      setMonthly((n * 8 * 22).toFixed(2));
    }
  }
  function handleDailyChange(val) {
    setDaily(val);
    const n = parseFloat(val);
    if (!isNaN(n) && n > 0) {
      setWeekly((n * 7).toFixed(2));
      setMonthly((n * 22).toFixed(2));
    }
  }

  async function handleSave() {
    setSaving(true); setError('');
    try {
      await api.patch(`/businesses/${businessId}/`, {
        hourly_target: hourly, daily_target: daily,
        weekly_target: weekly, monthly_target: monthly,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  }

  return (
    <div className={styles.targetForm}>
      <div className={styles.targetFormTitle}>Revenue Targets</div>
      {error && <p className={styles.targetFormError}>{error}</p>}
      <div className={styles.targetFormFields}>
        {[
          { label: 'Hourly',   val: hourly,  set: handleHourlyChange,       ph: 'e.g. 1500' },
          { label: 'Daily',    val: daily,   set: handleDailyChange,         ph: 'e.g. 12000' },
          { label: 'Weekly',   val: weekly,  set: v => setWeekly(v),         ph: 'e.g. 60000' },
          { label: 'Monthly',  val: monthly, set: v => setMonthly(v),        ph: 'e.g. 240000' },
        ].map(f => (
          <label key={f.label} className={styles.targetFormLabel}>
            <span>{f.label}</span>
            <input
              type="number" min="0" step="0.01"
              value={f.val} onChange={e => f.set(e.target.value)}
              className={styles.targetFormInput} placeholder={f.ph}
            />
          </label>
        ))}
      </div>
      <div className={styles.targetFormActions}>
        <button className={styles.targetFormSave} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Targets'}
        </button>
        <button className={styles.targetFormCancel} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Per-business section ──────────────────────────────────────────────────────
function BusinessSection({ businessId }) {
  const [data,           setData]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [showTargetForm, setShowTargetForm] = useState(false);
  const mounted = useRef(true);

  const fetchData = useCallback(() => {
    setLoading(true);
    api.get(`/analytics/${businessId}/target-progress/`)
      .then(res => { if (res.data.success && mounted.current) setData(res.data.data); })
      .catch(() => {})
      .finally(() => { if (mounted.current) setLoading(false); });
  }, [businessId]);

  useEffect(() => {
    mounted.current = true;
    fetchData();
    return () => { mounted.current = false; };
  }, [fetchData]);

  if (loading) return <div className={styles.sectionLoading}>Loading…</div>;
  if (!data)   return null;

  const hasTargets = data.daily_target || data.weekly_target;

  return (
    <div className={styles.businessSection}>
      {/* Header */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionName}>{data.business_name}</h2>
        <div className={styles.sectionHeaderRight}>
          <StatusBadge pct={data.daily_pct} />
          <button
            className={styles.editTargetsBtn}
            onClick={() => setShowTargetForm(v => !v)}
          >
            {showTargetForm ? 'Cancel' : hasTargets ? 'Edit targets' : 'Set targets'}
          </button>
        </div>
      </div>

      {showTargetForm ? (
        <TargetForm
          businessId={businessId}
          data={data}
          onSaved={() => { setShowTargetForm(false); fetchData(); }}
          onCancel={() => setShowTargetForm(false)}
        />
      ) : (
        <>
          {/* Hero banner */}
          <HeroBanner data={data} />

          {/* Four KPI cards */}
          <FourKpiCards data={data} />

          {/* Hourly chart + Income vs Expenses */}
          <div className={styles.twoCol}>
            <HourlyRevenueChart data={data} />
            <IncomeExpensesPanel data={data} />
          </div>

          {/* Revenue trend + Modality trend (left) | Smart alerts spanning both (right) */}
          <div className={styles.chartsAndAlerts}>
            <div className={styles.chartsCol}>
              <RevenueChart businessId={businessId} />
              <ModalityTrend businessId={businessId} />
            </div>
            <SmartAlerts data={data} />
          </div>
        </>
      )}
    </div>
  );
}

// ── Business dropdown ─────────────────────────────────────────────────────────
function verdictDotColor(verdict) {
  if (!verdict) return '#d1d5db';
  const v = verdict.toUpperCase();
  if (v === 'EXCELLENT' || v === 'GOOD')           return C.teal;
  if (v === 'AVERAGE'   || v === 'WATCH')          return C.amber;
  return C.red;
}

function BusinessDropdown({ businesses, selectedId, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selected = businesses.find(b => b.business_id === selectedId);

  return (
    <div className={styles.bizDrop} ref={ref}>
      <button
        className={`${styles.bizDropBtn} ${open ? styles.bizDropBtnOpen : ''}`}
        onClick={() => setOpen(v => !v)}
      >
        <span className={styles.bizDropDot} style={{ background: verdictDotColor(selected?.verdict) }} />
        <span className={styles.bizDropName}>{selected?.business_name || 'Select business'}</span>
        <svg
          className={`${styles.bizDropChevron} ${open ? styles.bizDropChevronOpen : ''}`}
          width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className={styles.bizDropMenu}>
          {businesses.map(b => (
            <button
              key={b.business_id}
              className={`${styles.bizDropItem} ${b.business_id === selectedId ? styles.bizDropItemActive : ''}`}
              onClick={() => { onChange(b.business_id); setOpen(false); }}
            >
              <span className={styles.bizDropItemDot} style={{ background: verdictDotColor(b.verdict) }} />
              <span className={styles.bizDropItemName}>{b.business_name}</span>
              {b.verdict && (
                <span className={styles.bizDropItemVerdict}>{b.verdict}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard() {
  const [businesses,    setBusinesses]    = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [selectedId,    setSelectedId]    = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [now,           setNow]           = useState(new Date());

  useEffect(() => {
    async function load() {
      try {
        const compRes = await api.get('/analytics/comparison/');
        if (compRes.data.success) {
          const list = compRes.data.data || [];
          setBusinesses(list);
          // Prefer the business with the most recent verdict; fallback to first
          const withVerdict = list
            .filter(b => b.verdict_date)
            .sort((a, b) => (b.verdict_date > a.verdict_date ? 1 : -1));
          const best = withVerdict[0] || list[0];
          if (best) setSelectedId(best.business_id);
        }
      } catch { /* no data yet */ }
      finally { setLoading(false); }

      // Notifications are non-critical — load independently so failure doesn't block UI
      try {
        const notifRes = await api.get('/notifications/', { params: { unread_only: true } });
        if (notifRes.data.success) setNotifications(notifRes.data.data?.notifications || []);
      } catch { /* notifications optional */ }
    }
    load();
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const timeStr = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });

  // Top-right actions: live timer + notification + business dropdown
  const headerActions = (
    <div className={styles.headerActions}>
      <div className={styles.headerActionsTop}>
        <span className={styles.liveDot} />
        <span className={styles.liveLbl}>Live</span>
        <span className={styles.timeLbl}>{timeStr}</span>
        {notifications.length > 0 && (
          <span className={styles.notifBadge}>
            {notifications.length} unread
          </span>
        )}
      </div>
      {businesses.length > 1 && (
        <BusinessDropdown
          businesses={businesses}
          selectedId={selectedId}
          onChange={setSelectedId}
        />
      )}
    </div>
  );

  if (loading) {
    return (
      <div>
        <PageHeader title="Dashboard" subtitle="Income overview" />
        <div className={styles.loadingText}>Loading dashboard…</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Income overview" actions={headerActions} />

      {businesses.length === 0 ? (
        <div className={styles.emptyState}>
          <h3>Welcome to Trucount</h3>
          <p>Add a business and start submitting daily entries to see income data here.</p>
        </div>
      ) : selectedId ? (
        <BusinessSection key={selectedId} businessId={selectedId} />
      ) : null}
    </div>
  );
}

export default Dashboard;
